import { useMemo, useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import { Slate, Editable, withReact, useSlateStatic, ReactEditor } from 'slate-react';
import { Transforms, createEditor, Range, Editor, Point } from 'slate';
import { withHistory } from 'slate-history';
import styles from './FJRichTextEditor.module.less';
import FJDropDown from '../../../common/components/FJDropDown/FJDropDown';
import FJBlueArrow from '../../../assets/svg/textToVideo/blue-arrow.svg?react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FJUtil from '../../../util/FJUtil';
const FJRichTextEditor = props => {
    const { value, onChange, placeholder, maxHeight, minHeight = 28 } = props;
    const editor = useMemo(() => withHistory(withReact(createEditor())), []);
    const [isComposing, setIsComposing] = useState(false);
    const editorRef = useRef(null);
    const [height, setHeight] = useState(28);
    const renderElement = useCallback(
        props => <Element {...props} isComposing={isComposing} onChange={onChange} />,
        [isComposing, onChange],
    );
    const _onBlur = useCallback(() => {
        onChange(editor.children);
    }, []);

    // 计算高度

    const _updateHeight = useCallback(nodes => {
        if (!editorRef.current) return;
        const { width } = editorRef.current.getBoundingClientRect();
        if (width) {
            const scriptInputValue = FJUtil.getTextBySlateNodes(nodes);
            const placeholders = nodes.reduce(
                (calc, item) => calc + (item.type === 'input' && !item.children[0].text.trim() ? item.placeholder : ''),
                '',
            );
            const selectExtraWidth = nodes.reduce((calc, item) => calc + (item.type === 'select' ? 35 : 0), 0);
            const inputExtraWidth = nodes.reduce((calc, item) => calc + (item.type === 'input' ? 10 : 0), 0);
            let { width: textWidth } = FJUtil.calcTextSize(scriptInputValue + placeholders, {
                fontSize: '15px',
                fontFamily: 'Roboto, Roboto-Medium',
                fontWeight: 400,
                fontHeight: 26,
            });
            textWidth += (!!placeholders ? 24 : 0) + selectExtraWidth + inputExtraWidth;
            const line = Math.ceil(textWidth / width);
            const height = line * 26 + line * 2;
            const realHeight = Math.min(Math.max(height, minHeight), maxHeight);
            setHeight(realHeight);
        }
    }, []);
    const _onChange = useCallback(
        nodes => {
            _updateHeight(nodes);
        },
        [_updateHeight],
    );
    useEffect(() => {
        _updateHeight(editor.children);
    }, [editor.children, _updateHeight]);
    const _onKeyDown = useCallback(
        e => {
            // e.stopPropagation();
            const { selection } = editor;
            const [currentNode, path] = Editor.node(editor, selection);
            const start = Editor.start(editor, path);
            const end = Editor.end(editor, path);
            const inputNodeIndex = editor.children.findIndex(node => node.type === 'input');
            // 特殊处理输入块光标移动
            if (e.nativeEvent.key === 'ArrowLeft') {
                // if (Point.equals(selection.anchor, start)) {
                //     e.preventDefault();
                //     let distance = 2;
                //     if (path[0] === inputNodeIndex + 1 && !editor.children[inputNodeIndex].children[0].text) {
                //         distance = 1;
                //     }
                //     Transforms.move(editor, { unit: 'offset', reverse: true, distance });
                // }
            } else if (e.nativeEvent.key === 'ArrowRight') {
                // if (Point.equals(selection.focus, end)) {
                //     e.preventDefault();
                //     let distance = 2;
                //     if (path[0] === inputNodeIndex - 1 && !editor.children[inputNodeIndex].children[0].text) {
                //         distance = 1;
                //     }
                //     Transforms.move(editor, { unit: 'offset', distance });
                // }
            } else if (e.nativeEvent.key === 'Enter') {
                e.preventDefault();
            } else if (e.nativeEvent.key === 'Backspace' && selection && Range.isCollapsed(selection)) {
                // 如果光标在节点的开始位置，阻止默认行为（即合并节点）
                if (
                    Point.equals(selection.anchor, start) &&
                    (path[0] === inputNodeIndex + 1 || path[0] === inputNodeIndex)
                ) {
                    // 向前移动一个字符
                    Transforms.move(editor, { unit: 'offset', reverse: true, distance: 1 });
                }
            }
            //判断在input输入框是否预输入
            if (e.nativeEvent.key === 'Process' && path[0] === inputNodeIndex) {
                setIsComposing(true);
            }
        },
        [editor],
    );

    useEffect(() => {
        if (value) {
            // nodesRef.current = value;
            while (editor.children.length > 0) {
                Transforms.removeNodes(editor, { at: [0] }); // 从第一个节点开始删除
            }
            // 插入新的节点
            Transforms.insertNodes(editor, value); // 插入新的节点
        }
    }, [value]);

    const _renderPlaceholder = useCallback(props => {
        const { attributes, children } = props;
        return (
            <span className={styles.placeholder} {...attributes}>
                {children}
            </span>
        );
    }, []);
    // 采用此事件判断预输入 slate库 会报错
    // const _onCompositionStart = useCallback(
    //     e => {
    //         const { selection } = editor;
    //         const [currentNode, path] = Editor.node(editor, selection);
    //         const inputNodeIndex = editor.children.findIndex(node => node.type === 'input');
    //         if (path[0] === inputNodeIndex) {
    //             setIsComposing(true);
    //         }
    //     },
    //     [editor],
    // );

    const _onCompositionEnd = useCallback(e => {
        setIsComposing(false);
    }, []);

    return (
        <Slate editor={editor} initialValue={value} value={value} onChange={_onChange}>
            <Editable
                className={styles.editor}
                renderElement={renderElement}
                placeholder={placeholder}
                autoFocus
                onBlur={_onBlur}
                onKeyDown={_onKeyDown}
                renderPlaceholder={_renderPlaceholder}
                // onCompositionStart={_onCompositionStart}
                onCompositionEnd={_onCompositionEnd}
                ref={editorRef}
                style={{ height }}
            />
        </Slate>
    );
};

const Element = props => {
    const { attributes, children, element, isComposing, onChange } = props;
    switch (element.type) {
        case 'select':
            return <SlateSelect {...props} onChange={onChange} />;
        case 'input':
            return <SlateInput {...props} isComposing={isComposing} />;
        case 'paragraph':
            return <SlateParagraph {...props} />;
        default:
            return <p {...attributes}>{children}</p>;
    }
};
const SlateSelect = ({ attributes, children, element, onChange }) => {
    const { options, value } = element;
    const editor = useSlateStatic();
    const dropDownRef = useRef(null);
    const _overlay = (
        <ul className={styles.select_overlay}>
            {options.map(option => (
                <li
                    className={styles.option}
                    value={option}
                    key={option}
                    onClick={() => {
                        const path = ReactEditor.findPath(editor, element);
                        const newProperties = {
                            value: option,
                        };
                        Transforms.setNodes(editor, newProperties, { at: path });
                        dropDownRef.current.hide();
                        onChange(editor.children);
                    }}
                >
                    {option}
                </li>
            ))}
        </ul>
    );
    return (
        <>
            <FJDropDown
                ref={dropDownRef}
                trigger={'click'}
                overlay={_overlay}
                placement='topCenter'
                isTopLayer={true}
                onTriggerEleClick={() => {
                    document.activeElement && document.activeElement.blur();
                }}
            >
                <div className={styles.select} {...attributes} contentEditable={false}>
                    <span className={styles.select_value} contentEditable={false}>
                        {value}
                    </span>
                    {children}
                    <FJBlueArrow className={styles.arrow} />
                </div>
            </FJDropDown>
        </>
    );
};
const SlateInput = ({ attributes, children, element, isComposing }) => {
    const { placeholder } = element;
    const [minWidth, setMinWidth] = useState({});
    const isShowPlaceholder = useMemo(() => {
        return element.children[0].text.trim().length === 0 && !isComposing;
    }, [element, isComposing]);

    useLayoutEffect(() => {
        if (isShowPlaceholder && !isComposing) {
            let { width } = FJUtil.calcTextSize(placeholder, {
                fontSize: '15px',
                fontFamily: 'Roboto, Roboto-Medium',
                fontWeight: 400,
                fontHeight: 26,
            });
            setMinWidth({ minWidth: width + 20 });
        } else {
            setMinWidth({});
        }
    }, [isShowPlaceholder, children, element, isComposing, placeholder]);

    return (
        <div
            className={styles.input}
            contentEditable={true}
            suppressContentEditableWarning
            {...attributes}
            style={minWidth}
        >
            <span
                contentEditable={false}
                className={classNames(styles.placeholder_box, isShowPlaceholder && styles.show)}
            >
                <span contentEditable={false} className={styles.placeholder}>
                    {placeholder}
                </span>
            </span>
            <span style={{ fontSize: 0, userSelect: 'none', lineHeight: 0 }} contentEditable={false}>
                &nbsp;
            </span>
            {children}

            <span style={{ fontSize: 0, userSelect: 'none', lineHeight: 0 }} contentEditable={false}>
                &nbsp;
            </span>
        </div>
    );
};
const SlateParagraph = ({ attributes, children, element }) => {
    return (
        <span className={styles.paragraph} {...attributes}>
            {children}
        </span>
    );
};
FJRichTextEditor.propTypes = {
    textData: PropTypes.object,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
};

export default FJRichTextEditor;
